import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

export const ScreenshotProtection = ({ children }) => {
  const { user, isAdmin } = useAuth();
  const [isBlurred, setIsBlurred] = useState(false);

  useEffect(() => {
    // Only apply protection for non-admin users
    if (isAdmin) return;

    // Blur when window loses focus (user switching to screenshot app)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsBlurred(true);
      } else {
        // Small delay before unblurring to catch quick screenshots
        setTimeout(() => setIsBlurred(false), 500);
      }
    };

    const handleBlur = () => {
      setIsBlurred(true);
    };

    const handleFocus = () => {
      setTimeout(() => setIsBlurred(false), 300);
    };

    // Disable right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable keyboard shortcuts
    const handleKeyDown = (e) => {
      // Disable Print (Ctrl+P)
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        return false;
      }
      // Disable Save (Ctrl+S)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        return false;
      }
      // Disable Print Screen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        setIsBlurred(true);
        setTimeout(() => setIsBlurred(false), 1000);
        return false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAdmin]);

  // Admin sees everything normally
  if (isAdmin) {
    return <>{children}</>;
  }

  return (
    <div className="relative">
      {/* Content with blur protection */}
      <div 
        className={`transition-all duration-300 ${isBlurred ? 'blur-xl select-none pointer-events-none' : ''}`}
        style={{ 
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none'
        }}
      >
        {children}
      </div>

      {/* Watermark overlay */}
      <div 
        className="fixed inset-0 pointer-events-none z-50 overflow-hidden opacity-[0.03]"
        style={{ userSelect: 'none' }}
      >
        <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-20 -rotate-45 scale-150">
          {Array.from({ length: 50 }).map((_, i) => (
            <span 
              key={i} 
              className="text-2xl font-bold text-stone-900 whitespace-nowrap"
            >
              {user?.username || 'User'} • VPrep
            </span>
          ))}
        </div>
      </div>

      {/* Blur overlay message */}
      {isBlurred && (
        <div className="fixed inset-0 flex items-center justify-center z-40 bg-white/80">
          <div className="text-center p-8 bg-white rounded-2xl shadow-xl max-w-md mx-4">
            <div className="w-16 h-16 rounded-full bg-[#124242] flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
                <circle cx="12" cy="12" r="3"/>
                <path d="m2 2 20 20"/>
              </svg>
            </div>
            <h2 className="text-xl font-bold text-stone-800 mb-2">Content Protected</h2>
            <p className="text-stone-600">
              Recipe content is protected. Please return to the app to continue viewing.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
